/**
 * The Home overview (`GET /api/v1/overview`) and each store's setup steps,
 * in the order they have to be done. Home shows them as a checklist; the
 * welcome tour reads them to choose its version and name the next step.
 */

import type { HelpArticle } from "./help-slugs";

export interface OverviewStore {
  id: string;
  name: string;
  platform: string;
  status: "active" | "paused" | "error";
  last_synced_at: string | null;
  last_sync_error: string | null;
  products: number;
  contacts: number;
  mail: { provider: string; audience_name: string | null; status: "active" | "error" } | null;
  brand_saved: boolean;
  campaigns: number;
  drafts_waiting: number;
  schedules: number;
  /** The next campaign already approved and scheduled. */
  next_send_at: string | null;
  /** The next send a schedule plans; its draft appears two days before. */
  next_planned_at: string | null;
  /** The server's checklist (`store-setup.ts`), in order: the same steps the assistant explains. */
  setup: { key: string; label: string; done: boolean; optional: boolean }[];
}

export interface Overview {
  plan: {
    tier: string;
    label: string;
    is_trial: boolean;
    max_stores: number | null;
    max_contacts: number | null;
    campaign_limit: number | null;
    campaigns_used: number;
    /** How far ahead a schedule's draft is made. */
    lead_hours?: number;
  };
  stores: OverviewStore[];
}

export interface Step {
  key: string;
  label: string;
  done: boolean;
  optional: boolean;
  /** Where to do it. */
  to: string;
  action: string;
  /** How, in a sentence or two: shown under the step that is next. */
  how: string;
  /** The help article on it. */
  article: HelpArticle;
}

/** Where each of the server's steps is done (the server names it). A key this screen doesn't know is left out. */
const STEP_TEXT: Record<string, Pick<Step, "action" | "how" | "article"> & { to: (campaigns: string) => string }> = {
  store: {
    to: () => "/stores",
    action: "Open Stores",
    how: "Choose your store's platform on Stores and fill in what it asks for: your store's address and the key you make in its admin.",
    article: "setup-checklist",
  },
  mail: {
    to: () => "/stores",
    action: "Connect it in Stores",
    how: "On your store's card, under Mail service, choose Mailchimp, Klaviyo or Brevo. Mailchimp opens a window to sign in and allow Lettrova; Klaviyo and Brevo ask for a key you copy from their settings.",
    article: "connect-mailchimp",
  },
  audience: {
    to: () => "/stores",
    action: "Choose it in Stores",
    how: "On your store's card, pick the audience (list) your customers go into. Only customers who agreed to marketing become subscribers there.",
    article: "change-audience",
  },
  brand: {
    to: (c) => `${c}&tab=brand`,
    action: "Set up Brand",
    how: "Add the name your emails come from, a reply-to address, your colors and a few words about how your shop talks, then save.",
    article: "brand-settings",
  },
  first_campaign: {
    to: (c) => c,
    action: "Draft a campaign",
    how: "Draft one by hand to see what Lettrova writes, or add a schedule and drafts arrive by themselves. Nothing is sent until you approve it.",
    article: "how-drafts-are-written",
  },
  script: {
    to: () => "/stores",
    action: "Get it in Stores",
    how: "Paste one line into your storefront so Lettrova can tell which orders came from which campaign.",
    article: "tracking-script",
  },
  sender: {
    to: (c) => `${c}&tab=brand`,
    action: "See it in Brand",
    how: "Authenticate your reply-to address's domain at your email service, so campaigns arrive from your own domain and land in inboxes.",
    article: "sending-domain",
  },
};

/** What a store needs before campaigns can go out, in the order it has to happen. */
export function setupSteps(store: OverviewStore): Step[] {
  const campaigns = `/campaigns?store=${store.id}`;
  return store.setup.flatMap((s) => {
    const text = STEP_TEXT[s.key];
    return text
      ? [{ key: s.key, label: s.label, done: s.done, optional: s.optional, to: text.to(campaigns), action: text.action, how: text.how, article: text.article }]
      : [];
  });
}
